import { AspectRatio, ChakraProps, Image } from '@chakra-ui/react';
import { CMS } from '@waitroom/models';
import { useState } from 'react';

import Modal from '../../../core/components/Modal/Modal';
import Video from '../../../core/components/Video/Video';
import { commonConfig } from '../../../core/config';
import { getFileUrl, getImageUrl } from '../../utils/base';

const videoProps = {
  rounded: '1.2rem',
  overflow: 'hidden',
} as const;

export interface CmsVideoOrImageProps extends ChakraProps {
  video?: CMS.Video;
  image?: CMS.Image;
  size?: [number, number];
  fallbackSrc?: string;
}

const FullLengthVideo = ({
  video,
  fullVideo,
  poster,
  autoplay,
  ...rest
}: {
  video: string;
  fullVideo: string;
  poster?: string;
  autoplay?: boolean;
} & ChakraProps) => {
  const [play, setPlay] = useState(false);

  return (
    <>
      <AspectRatio w="full" ratio={16 / 9}>
        <Video
          video={[{ src: `${video}`, type: 'video/mp4' }]}
          poster={poster}
          autoPlay={autoplay}
          lazy
          loop
          muted
          playsInline
          onClick={() => setPlay(true)}
          cursor="pointer"
          {...videoProps}
          {...rest}
        />
      </AspectRatio>
      <Modal
        size="6xl"
        isOpen={!!play}
        onClose={() => setPlay(false)}
        isCentered
        contentProps={{ py: 0, overflow: 'hidden' }}
        forceCenter
      >
        <Modal.Header position="absolute" zIndex={5} top={0} right={0} />
        <Modal.Body px={0}>
          <AspectRatio w="full" ratio={16 / 9}>
            <Video
              video={[{ src: play ? `${fullVideo}` : '', type: 'video/mp4' }]}
              poster={poster}
              autoPlay={autoplay}
              playsInline
              controls
              border="none"
              rounded={0}
              {...rest}
            />
          </AspectRatio>
        </Modal.Body>
      </Modal>
    </>
  );
};

const CmsVideoOrImage = ({
  video,
  image,
  size,
  fallbackSrc,
  ...rest
}: CmsVideoOrImageProps): React.ReactElement | null => {
  const { file, fileFull } = video || {};
  const videoUrl = getFileUrl(file?.asset._ref);
  const fullUrl = getFileUrl(fileFull?.asset._ref);
  const imageUrl = image
    ? getImageUrl(image.asset._ref, size ? ['fit=crop', `w=${size[0]}`, `h=${size[1]}`] : [])
    : undefined;

  return (
    <>
      {videoUrl ? (
        fullUrl ? (
          <FullLengthVideo
            video={videoUrl}
            fullVideo={fullUrl}
            poster={imageUrl}
            autoplay={!!video && !!video.autoplay}
            {...rest}
          />
        ) : (
          <Video
            video={[{ src: `${videoUrl}`, type: 'video/mp4' }]}
            poster={imageUrl}
            autoPlay={!!video && !!video.autoplay}
            lazy
            loop
            muted
            playsInline
            {...videoProps}
            {...rest}
          />
        )
      ) : imageUrl ? (
        <Image
          src={imageUrl}
          fallbackSrc={fallbackSrc}
          alt={commonConfig.company.name}
          loading="lazy"
          rounded="xl"
          {...rest}
        />
      ) : null}
    </>
  );
};
export default CmsVideoOrImage;
